import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { ApiError, get, post, type RundownDto, type RundownItemDto } from '../api.js';
import { PageTitle } from '../frame.js';
import { StoryStatusBadge } from '../status-badge.js';
import { CONTENT_ICON, contentsText } from '../storyline-icons.js';
import { t } from '../i18n.js';
import { Link, navigate } from '../router.js';
import { useSession } from '../session.js';
import { classes, fmtDateInput, hhmmss, mmss, rt } from '../util.js';
import { realtime, type WsMessage } from '../ws.js';
import { GraphicsDialog } from './graphics-dialog.js';
import { MediaPanel } from './media-panel.js';
import { AutocueDialog, CopyBulletinDialog, MediaDialog, PublishDialog, SelectedDialog } from './rundown-dialogs.js';

type Mutation =
  | { op: 'insert'; afterItemId: string | null; storyId: string | null; kind?: string; title?: string; displayNumber?: string | null }
  | { op: 'move'; itemId: string; toIndex: number }
  | { op: 'remove'; itemId: string }
  | { op: 'restore'; itemId: string }
  | { op: 'float'; itemId: string; floated: boolean }
  | { op: 'duplicate'; itemId: string; mode: 'copy' | 'linked' }
  | { op: 'update'; itemId: string; patch: Record<string, unknown> };

interface SyncLink {
  target: 'playout' | 'mos';
  targetRef: string;
  state: string;
  lastAppliedRevision: number | null;
  lastError: string | null;
  blockedItems: Array<{ itemId: string; reason: string }>;
  lastLatencyMs: number | null;
  /** The player of the VO clips as chosen; 'none' = on the linked player; null = the installation's. */
  voPlayer?: string | null;
}
interface ItemSync {
  itemId: string;
  target: string;
  state: string;
  downstreamStatus: string | null;
  blockedReason: string | null;
}
export interface SyncInfo {
  links: SyncLink[];
  items: ItemSync[];
}

export function RundownGridPage({ id }: { id: string }) {
  const { session, can } = useSession();
  const [rundown, setRundown] = useState<RundownDto | null>(null);
  const [sync, setSync] = useState<SyncInfo>({ links: [], items: [] });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cursor, setCursor] = useState(0);
  const [inline, setInline] = useState<{ itemId: string; number: string; title: string } | null>(null);
  const [drag, setDrag] = useState<{ id: string; over: string | null; after: boolean } | null>(null);
  const [presence, setPresence] = useState<Array<{ userId: string; displayName: string; editingStoryId: string | null }>>([]);
  const [leases, setLeases] = useState<Record<string, { displayName: string }>>({});
  const [changed, setChanged] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<'autocue' | 'selected' | 'copy' | 'publish' | 'removed' | null>(null);
  const [graphicsFor, setGraphicsFor] = useState<RundownItemDto | null>(null);
  const [mediaFor, setMediaFor] = useState<RundownItemDto | null>(null);
  const [mediaTitles, setMediaTitles] = useState<Map<string, string>>(new Map());
  const [panelOpen, setPanelOpen] = useState<boolean>(() => localStorage.getItem('airpost.grid.mediaPanel') === '1');
  const [graphicsVersion, setGraphicsVersion] = useState(0);
  const togglePanel = () =>
    setPanelOpen((o) => {
      localStorage.setItem('airpost.grid.mediaPanel', o ? '0' : '1');
      return !o;
    });
  const [removed, setRemoved] = useState<Array<{ id: string; title: string; displayNumber: string | null; removedAt: string }>>([]);
  const [columns, setColumns] = useState<{ type: boolean; playout: boolean; graphics: boolean; media: boolean }>(() => {
    try {
      return { type: false, playout: false, graphics: false, media: false, ...(JSON.parse(localStorage.getItem('airpost.grid.columns') ?? '') as object) } as never;
    } catch {
      return { type: false, playout: false, graphics: false, media: false };
    }
  });
  const tableRef = useRef<HTMLDivElement>(null);
  // titles of the playout clips linked to rows (mirrored media index), fetched when the set of ids changes
  const mediaIdsKey = (rundown?.items ?? []).map((it) => it.mediaAssetId).filter(Boolean).sort().join(',');
  useEffect(() => {
    if (!mediaIdsKey) return;
    let cancelled = false;
    get<{ media: Array<{ id: string; title: string }> }>(`/api/media?ids=${encodeURIComponent(mediaIdsKey)}&limit=200`)
      .then((r) => !cancelled && setMediaTitles(new Map(r.media.map((m) => [m.id, m.title]))))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [mediaIdsKey]);
  const prevItems = useRef<Map<string, string>>(new Map());
  // back from a story page (?item=): land on its row, once
  const jumpRef = useRef<string | null>(new URLSearchParams(window.location.search).get('item'));

  const load = useCallback(async () => {
    try {
      const r = await get<{ rundown: RundownDto; sync: SyncInfo }>(`/api/rundowns/${id}`);
      setRundown(r.rundown);
      setSync(r.sync);
      setError(null);
      const target = jumpRef.current;
      if (target) {
        jumpRef.current = null;
        const idx = r.rundown.items.findIndex((it) => it.id === target);
        if (idx >= 0) {
          setCursor(idx);
          window.setTimeout(() => document.getElementById(`row-${target}`)?.scrollIntoView({ block: 'center' }), 50);
        }
        navigate(`/rundown/${id}`, { replace: true });
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [id]);

  const onMessage = useCallback(
    (m: WsMessage) => {
      if (m.type === 'rundown' && m.rundownId === id) {
        const view = m.view as RundownDto;
        // highlight rows whose title/time changed under us
        const sig = (it: RundownItemDto) => `${it.title}|${it.readS}|${it.videoS}|${it.displayNumber}`;
        const next = new Set<string>();
        if (prevItems.current.size) {
          for (const it of view.items) {
            const old = prevItems.current.get(it.id);
            if (old !== undefined && old !== sig(it)) next.add(it.id);
          }
        }
        prevItems.current = new Map(view.items.map((it) => [it.id, sig(it)]));
        setRundown(view);
        if (next.size) {
          setChanged(next);
          window.setTimeout(() => setChanged(new Set()), 4000);
        }
      }
      if (m.type === 'sync' && m.rundownId === id) setSync({ links: m.links as SyncLink[], items: m.items as ItemSync[] });
      if (m.type === 'presence' && m.rundownId === id) setPresence(m.users as never);
      if (m.type === 'lease' && m.resourceType === 'story') {
        const rid = m.resourceId as string;
        setLeases((l) => {
          const n = { ...l };
          if (m.holder) n[rid] = m.holder as { displayName: string };
          else delete n[rid];
          return n;
        });
      }
    },
    [id],
  );

  useEffect(() => {
    // the subscription pushes the current view at once; the HTTP load covers a socket that is still connecting
    const unsub = realtime.subscribe(id);
    const off = realtime.listen(onMessage);
    const initial = window.setTimeout(() => void load(), 0);
    return () => {
      window.clearTimeout(initial);
      unsub();
      off();
    };
  }, [id, load, onMessage]);

  useEffect(() => {
    localStorage.setItem('airpost.grid.columns', JSON.stringify(columns));
  }, [columns]);

  const items = useMemo(() => rundown?.items ?? [], [rundown]);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const playoutState = useMemo(() => new Map(sync.items.filter((s) => s.target === 'playout').map((s) => [s.itemId, s])), [sync]);
  const mosState = useMemo(() => new Map(sync.items.filter((s) => s.target === 'mos').map((s) => [s.itemId, s])), [sync]);

  const mutate = useCallback(
    async (mutations: Mutation[]): Promise<boolean> => {
      if (!rundown) return false;
      try {
        const r = await post<{ ok: true; rundown: RundownDto; revision: number }>(`/api/rundowns/${id}/mutations`, { baseRevision: rundown.revision, mutationId: crypto.randomUUID(), mutations });
        setRundown(r.rundown);
        setNotice(null);
        return true;
      } catch (e) {
        if (e instanceof ApiError && e.code === 'CONFLICT') {
          const p = e.payload as { rundown: RundownDto };
          setRundown(p.rundown);
          setNotice(t('conflict'));
          return false;
        }
        setError(e instanceof ApiError ? e.message : String(e));
        return false;
      }
    },
    [id, rundown],
  );

  // ── selection ────────────────────────────────────────────────────────────────
  const toggle = (itemId: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(itemId)) n.delete(itemId);
      else n.add(itemId);
      return n;
    });

  // ── inline edit (Τροποποίηση Στον Οδηγό) ─────────────────────────────────────
  const startInline = (it: RundownItemDto) => setInline({ itemId: it.id, number: it.displayNumber ?? '', title: it.kind === 'separator' && it.title === '.' ? '' : it.title });
  const saveInline = async () => {
    if (!inline) return;
    const it = byId.get(inline.itemId);
    if (!it) return setInline(null);
    const number = inline.number.trim() === '' ? null : inline.number.trim();
    const patch: Record<string, unknown> = {};
    if (number !== it.displayNumber) patch.displayNumber = number;
    // a separator with no label keeps HPress's '.', so exports and the autocue still see a separator
    const title = it.kind === 'separator' && inline.title.trim() === '' ? '.' : inline.title;
    if (title !== it.title) patch.titleOverride = title;
    if (Object.keys(patch).length) await mutate([{ op: 'update', itemId: it.id, patch }]);
    setInline(null);
    tableRef.current?.focus();
  };

  // ── separators between stories ────────────────────────────────────────────────
  const addSeparator = async (afterItemId?: string | null) => {
    if (!can('story.create')) return;
    const after = afterItemId !== undefined ? afterItemId : (items[cursor]?.id ?? null);
    const at = after ? items.findIndex((i) => i.id === after) + 1 : 0;
    if (await mutate([{ op: 'insert', afterItemId: after, storyId: null, kind: 'separator', title: '.', displayNumber: null }])) setCursor(at);
    tableRef.current?.focus();
  };
  // whoever may put a separator in may take it out (it holds no story); it is kept with the cut rows, so it can come back
  const canRemoveSeparator = can('story.create') || can('story.cut');
  const removeSeparator = async (itemId: string) => {
    if (!canRemoveSeparator) return;
    await mutate([{ op: 'remove', itemId }]);
    tableRef.current?.focus();
  };

  // ── cut / restore ─────────────────────────────────────────────────────────────
  const cut = async () => {
    if (!selected.size) return setNotice(t('nothingSelected'));
    if (!window.confirm(t('confirmCut'))) return;
    const ok = await mutate([...selected].map((itemId) => ({ op: 'remove', itemId }) as Mutation));
    if (ok) setSelected(new Set());
  };
  const openRemoved = async () => {
    const r = await get<{ removed: typeof removed }>(`/api/rundowns/${id}/removed`);
    setRemoved(r.removed);
    setDialog('removed');
  };

  // ── keyboard ─────────────────────────────────────────────────────────────────
  const onKey = async (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (inline) return;
    const it = items[cursor];
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (e.altKey && it && can('rundown.reorder')) await mutate([{ op: 'move', itemId: it.id, toIndex: Math.min(items.length - 1, cursor + 1) }]).then((ok) => ok && setCursor((c) => Math.min(items.length - 1, c + 1)));
        else setCursor((c) => Math.min(items.length - 1, c + 1));
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (e.altKey && it && can('rundown.reorder')) await mutate([{ op: 'move', itemId: it.id, toIndex: Math.max(0, cursor - 1) }]).then((ok) => ok && setCursor((c) => Math.max(0, c - 1)));
        else setCursor((c) => Math.max(0, c - 1));
        return;
      case ' ':
        e.preventDefault();
        if (it) toggle(it.id);
        return;
      case 'Enter':
        e.preventDefault();
        if (it?.storyId) navigate(`/story/${it.storyId}/edit?from=${id}`);
        return;
      case 'F2':
        e.preventDefault();
        if (it) startInline(it);
        return;
      case 'Delete':
        e.preventDefault();
        if (it?.kind === 'separator') {
          await removeSeparator(it.id);
          return;
        }
        if (it && can('story.cut')) {
          setSelected(new Set([it.id]));
          if (window.confirm(t('confirmCut'))) await mutate([{ op: 'remove', itemId: it.id }]);
        }
        return;
      case 'F5':
        e.preventDefault();
        await load();
        return;
      case 'Insert':
        e.preventDefault();
        await addSeparator();
        return;
      case 'Home':
        setCursor(0);
        return;
      case 'End':
        setCursor(items.length - 1);
        return;
    }
  };

  // ── drag and drop ─────────────────────────────────────────────────────────────
  const onDrop = async () => {
    if (!drag || !drag.over || drag.over === drag.id) return setDrag(null);
    const from = items.findIndex((i) => i.id === drag.id);
    const overIdx = items.findIndex((i) => i.id === drag.over);
    let to = overIdx + (drag.after ? 1 : 0);
    if (from < to) to -= 1; // index after removal
    setDrag(null);
    if (from !== to) await mutate([{ op: 'move', itemId: drag.id, toIndex: to }]);
  };

  if (error) return <div className="banner err">{error}</div>;
  if (!rundown) return <div>{t('loading')}</div>;

  const playoutLink = sync.links.find((l) => l.target === 'playout');
  const mosLink = sync.links.find((l) => l.target === 'mos');
  const statusBadge = (s: string) => <span className={`status ${s === 'synced' ? 'ok' : s === 'pending' ? 'warn' : s === 'blocked' || s === 'failed' ? 'err' : 'info'}`}>{t(s as 'pending')}</span>;

  const toolbar = (
    <div className="toolbar">
      <button className="btn danger" onClick={cut} disabled={!can('story.cut')} title="Delete">
        ✂ {t('cut')}
      </button>
      <button className="btn" onClick={() => void addSeparator()} disabled={!can('story.create')} title={t('separatorAddHint')}>
        ⎯ {t('separator')}
      </button>
      <a className="btn" href={`/api/rundowns/${id}/exports/pdf-full`} target="_blank" rel="noreferrer">
        🖨 {t('printBulletin')}
      </a>
      <a className="btn" href={`/api/rundowns/${id}/exports/pdf-list`} target="_blank" rel="noreferrer">
        🖨 {t('printList')}
      </a>
      <button className="btn" onClick={() => setDialog('autocue')} disabled={!can('autocue.send')}>
        {t('autocue')}
      </button>
      <button className="btn" onClick={() => (selected.size ? setDialog('selected') : setNotice(t('nothingSelected')))}>
        ☰ {t('showSelected')}
      </button>
      <button className="btn" onClick={() => setDialog('copy')} disabled={!can('rundown.reorder')}>
        ⧉ {t('copyBulletin')}
      </button>
      <Link className="btn" to={`/story/new?rundownId=${id}`} onClick={(e) => !can('story.create') && e.preventDefault()}>
        ✚ {t('newStory')}
      </Link>
      <button className="btn" onClick={() => void load()} title="F5">
        ↻ {t('refresh')}
      </button>
      <button className={classes('btn', panelOpen && 'active')} onClick={togglePanel} aria-pressed={panelOpen} title={t('mediaPanel')}>
        🎬 {t('mediaPanel')}
      </button>
      <span style={{ width: 12 }} />
      <Link className="btn" to={`/control/${id}`} title={t('crPickHint')}>
        📺 {t('crTitle')}
      </Link>
      <Link className="btn" to={`/playout/${id}`} title={t('controlTitle')}>
        🎛 {t('controlShort')}
      </Link>
      <button className="btn primary" onClick={() => setDialog('publish')} disabled={!can('rundown.publish')}>
        ⇪ {t('publish')} {rundown.status !== 'draft' && <span className="status" style={{ background: '#fff', color: '#336699' }}>{t(rundown.status as 'linked')}</span>}
      </button>
    </div>
  );

  const optionalColumns = [columns.type, columns.playout, columns.media, columns.graphics].filter(Boolean).length;
  const canAdd = can('story.create');

  const editingBy = (storyId: string | null) => (storyId ? (leases[storyId]?.displayName ?? presence.find((p) => p.editingStoryId === storyId && p.userId !== session?.user.id)?.displayName ?? null) : null);

  return (
    <>
      <PageTitle title={`${t('gridTitle')} ${rundown.programmeName} ${fmtDateInput(rundown.date)}`} hint={t('gridHint')} />
      {toolbar}
      <div className="infobar">
        <span>
          {t('newsCount')}: {rundown.count} - {t('bulletinDate')}:{fmtDateInput(rundown.date)} - {t('bulletin')}:{rundown.programmeName} - {t('bulletinTime')}: {hhmmss(rundown.totalS)}
          {rundown.targetDurationS ? <span className="muted"> · στόχος {hhmmss(rundown.targetDurationS)} ({rundown.totalS > rundown.targetDurationS ? '+' : '-'}{mmss(Math.abs(rundown.totalS - rundown.targetDurationS))})</span> : null}
        </span>
        <span className="muted">
          {t('revision')} {rundown.revision}
          {playoutLink && <> · Playout {statusBadge(playoutLink.state)}</>}
          {mosLink && <> · Viz/MOS {statusBadge(mosLink.state)}</>}
          {presence.length > 0 && <> · {t('present')}: {presence.map((p) => p.displayName).join(', ')}</>}
        </span>
      </div>
      {notice && (
        <div className="banner warn">
          {notice}
          <button className="btn" onClick={() => setNotice(null)}>
            OK
          </button>
        </div>
      )}
      <p className="tip">{t('tipCopy')}</p>
      <p className="tip">{t('tipDash')}</p>
      <p className="tip">{t('tipKeys')}</p>
      <div className="links">
        <a href="#all" onClick={(e) => (e.preventDefault(), setSelected(new Set(items.map((i) => i.id))))}>
          {t('selectAll')}
        </a>
        <a href="#none" onClick={(e) => (e.preventDefault(), setSelected(new Set()))}>
          {t('selectNone')}
        </a>
        <a href="#removed" onClick={(e) => (e.preventDefault(), void openRemoved())}>
          {t('removed')}…
        </a>
        <span style={{ marginLeft: 16 }}>
          Στήλες:{' '}
          {(['type', 'playout', 'media', 'graphics'] as const).map((c) => (
            <label key={c} style={{ marginRight: 8 }}>
              <input type="checkbox" checked={columns[c]} onChange={(e) => setColumns({ ...columns, [c]: e.target.checked })} /> {c === 'type' ? t('colType') : c === 'playout' ? t('colPlayout') : c === 'media' ? t('colMedia') : t('colGraphics')}
            </label>
          ))}
        </span>
      </div>
      <div className={panelOpen ? 'grid-with-panel' : undefined}>
      <div ref={tableRef} className="grid-scroll" tabIndex={0} onKeyDown={(e) => void onKey(e)} style={{ outline: 'none' }}>
        <table className="grid" onDragOver={(e) => e.preventDefault()} onDrop={() => void onDrop()}>
          <colgroup>
            <col style={{ width: 25 }} />
            <col style={{ width: 71 }} />
            <col style={{ width: 51 }} />
            <col />
            <col style={{ width: 31 }} />
            <col style={{ width: 61 }} />
            <col style={{ width: 61 }} />
            <col style={{ width: 61 }} />
            <col style={{ width: 61 }} />
            <col style={{ width: 41 }} />
            {columns.type && <col style={{ width: 70 }} />}
            {columns.playout && <col style={{ width: 90 }} />}
            {columns.media && <col style={{ width: 170 }} />}
            {columns.graphics && <col style={{ width: 70 }} />}
          </colgroup>
          <thead>
            <tr>
              <th>{t('colSel')}</th>
              <th>{t('colActions')}</th>
              <th>{t('colNum')}</th>
              <th>{t('colTitle')}</th>
              <th>{t('colCat')}</th>
              <th>{t('colRead')}</th>
              <th>{t('colVideo')}</th>
              <th>{t('colTotal')}</th>
              <th>{t('colRt')}</th>
              <th>{t('colOrder')}</th>
              {columns.type && <th>{t('colType')}</th>}
              {columns.playout && <th>{t('colPlayout')}</th>}
              {columns.media && <th>{t('colMedia')}</th>}
              {columns.graphics && <th>{t('colGraphics')}</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const isInline = inline?.itemId === it.id;
              const lock = editingBy(it.storyId);
              const ps = playoutState.get(it.id);
              const ms = mosState.get(it.id);
              return (
                <tr
                  key={it.id}
                  id={`row-${it.id}`}
                  className={classes(idx === cursor && 'cursor', it.floated && 'floated', it.kind === 'separator' && 'separator', drag?.id === it.id && 'dragging', drag?.over === it.id && (drag.after ? 'drop-after' : 'drop-before'), changed.has(it.id) && 'changed', ps?.downstreamStatus === 'playing' && 'onair', ps?.downstreamStatus === 'cued' && 'next')}
                  draggable={can('rundown.reorder') && !isInline}
                  onDragStart={(e) => {
                    setDrag({ id: it.id, over: null, after: false });
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    const rect = (e.currentTarget as HTMLTableRowElement).getBoundingClientRect();
                    const after = e.clientY > rect.top + rect.height / 2;
                    setDrag((d) => (d && (d.over !== it.id || d.after !== after) ? { ...d, over: it.id, after } : d));
                  }}
                  onDragEnd={() => setDrag(null)}
                  onClick={() => setCursor(idx)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="c">
                    <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} aria-label={t('colSel')} />
                  </td>
                  <td className="actions">
                    {isInline ? (
                      <>
                        <button title={t('save')} onClick={() => void saveInline()}>
                          💾
                        </button>
                        <button title={t('cancel')} onClick={() => setInline(null)}>
                          ✖
                        </button>
                      </>
                    ) : it.kind === 'separator' ? (
                      <>
                        <button className="icon-guide" title={t('separatorLabel')} onClick={() => startInline(it)} />
                        {canRemoveSeparator && (
                          <button className="sep-remove" title={t('separatorRemove')} onClick={() => void removeSeparator(it.id)}>
                            ✕
                          </button>
                        )}
                        <RowMenu item={it} canReorder={can('rundown.reorder')} onAction={(m) => void mutate([m])} onGraphics={() => setGraphicsFor(it)} />
                      </>
                    ) : (
                      <>
                        <button className="icon-guide" title={t('editInGuide')} onClick={() => startInline(it)} />
                        <button className="icon-edit" title={t('editRecord')} disabled={!it.storyId} onClick={() => it.storyId && navigate(`/story/${it.storyId}/edit?from=${id}`)} />
                        <button className="icon-view" title={t('viewRecord')} disabled={!it.storyId} onClick={() => it.storyId && navigate(`/story/${it.storyId}?from=${id}`)} />
                        <RowMenu item={it} canReorder={can('rundown.reorder')} onAction={(m) => void mutate([m])} onGraphics={() => setGraphicsFor(it)} />
                      </>
                    )}
                    {lock && <div className="lease">✎ {lock}</div>}
                  </td>
                  {isInline ? (
                    <>
                      <td className="inline">
                        <input value={inline.number} onChange={(e) => setInline({ ...inline, number: e.target.value })} onKeyDown={(e) => (e.key === 'Enter' ? void saveInline() : e.key === 'Escape' ? setInline(null) : null)} autoFocus />
                      </td>
                      <td className="inline">
                        <input value={inline.title} onChange={(e) => setInline({ ...inline, title: e.target.value })} onKeyDown={(e) => (e.key === 'Enter' ? void saveInline() : e.key === 'Escape' ? setInline(null) : null)} />
                      </td>
                    </>
                  ) : it.kind === 'separator' ? (
                    <>
                      <td className="num">{it.displayNumber ?? ''}</td>
                      <td className="sep" colSpan={7 + optionalColumns} onDoubleClick={() => startInline(it)} title={t('separatorLabel')}>
                        {it.title && it.title !== '.' && <span className="sep-label">{it.title}</span>}
                        {canAdd && <SeparatorAdd onClick={() => void addSeparator(it.id)} />}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="num">{it.displayNumber ?? '-'}</td>
                      <td className="title">
                        {it.title}
                        {it.contents?.length > 0 && (
                          <span className="rd-contents" title={contentsText(it.contents)} aria-label={contentsText(it.contents)}>
                            {it.contents.map((c) => (
                              <span key={c.kind} className={`rd-content ${c.kind}`}>
                                {CONTENT_ICON[c.kind].icon}
                                {c.n > 1 && <sub>{c.n}</sub>}
                              </span>
                            ))}
                          </span>
                        )}
                        {canAdd && <SeparatorAdd onClick={() => void addSeparator(it.id)} />}
                      </td>
                    </>
                  )}
                  {it.kind === 'separator' && !isInline ? null : (
                  <>
                  {/* Κατ.: the story's state in full, as on Ειδήσεις Ημέρας (the HPress category never reaches the rundown) */}
                  <td className="c cat">{it.storyId && it.storyStatus ? <StoryStatusBadge status={it.storyStatus} /> : it.category || '-'}</td>
                  <td className="num" title={it.readOverridden ? `${t('computed')}: ${mmss(it.readComputedS)}` : undefined} style={it.readOverridden ? { fontStyle: 'italic' } : undefined}>
                    {mmss(it.readS)}
                  </td>
                  <td className="num">{mmss(it.videoS)}</td>
                  <td className="num">{mmss(it.totalS)}</td>
                  <td className="num">{it.floated ? '' : rt(it.runningS)}</td>
                  <td className="num">{it.floated ? '⟂' : it.order}</td>
                  {columns.type && <td className="c">{it.kind}</td>}
                  {columns.playout && <td className="c">{ps ? <span className={`status ${ps.state === 'synced' ? 'ok' : ps.state === 'pending' ? 'warn' : 'err'}`}>{ps.downstreamStatus ?? ps.state}</span> : playoutLink ? <span className="status warn">{t('pending')}</span> : ''}</td>}
                  {columns.media && (
                    <td className="small media">
                      {it.mediaAssetId ? (
                        <a role="button" onClick={() => setMediaFor(it)} title={it.mediaAssetId}>
                          🎬 {mediaTitles.get(it.mediaAssetId) ?? it.mediaAssetId}
                        </a>
                      ) : it.mediaCode ? (
                        <a role="button" className="virtual" onClick={() => setMediaFor(it)} title={t('virtualWaiting')}>
                          ⌛ {it.mediaCode}
                        </a>
                      ) : it.storyId ? (
                        <a role="button" className="muted" onClick={() => setMediaFor(it)}>
                          ＋
                        </a>
                      ) : null}
                    </td>
                  )}
                  {columns.graphics && <td className="c">{ms ? <span className={`status ${ms.state === 'synced' ? 'ok' : 'warn'}`}>{ms.downstreamStatus ?? ms.state}</span> : ''}</td>}
                  </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {panelOpen && (
        <MediaPanel
          item={items[cursor] ?? null}
          canEdit={can('story.edit.own')}
          onAssign={async (mediaAssetId) => {
            const it = items[cursor];
            if (!it) return false;
            return mutate([{ op: 'update', itemId: it.id, patch: { mediaAssetId, mediaInMs: null, mediaOutMs: null } }]);
          }}
          onGraphics={() => items[cursor] && setGraphicsFor(items[cursor]!)}
          graphicsVersion={graphicsVersion}
          onClose={togglePanel}
        />
      )}
      </div>
      {toolbar}

      {dialog === 'autocue' && <AutocueDialog rundownId={id} onClose={() => setDialog(null)} />}
      {dialog === 'copy' && <CopyBulletinDialog rundownId={id} current={{ programmeId: rundown.programmeId, date: rundown.date }} onClose={() => setDialog(null)} />}
      {dialog === 'selected' && <SelectedDialog rundownId={id} items={items.filter((i) => selected.has(i.id))} onClose={() => setDialog(null)} />}
      {dialog === 'publish' && <PublishDialog rundown={rundown} sync={sync} onClose={() => setDialog(null)} onChanged={() => void load()} />}
      {graphicsFor && (
        <GraphicsDialog
          item={graphicsFor}
          onClose={() => {
            setGraphicsFor(null);
            setGraphicsVersion((v) => v + 1);
          }}
        />
      )}
      {mediaFor && (
        <MediaDialog
          item={mediaFor}
          currentTitle={mediaFor.mediaAssetId ? (mediaTitles.get(mediaFor.mediaAssetId) ?? null) : null}
          onClose={() => setMediaFor(null)}
          onAssign={async (mediaAssetId) => {
            const ok = await mutate([{ op: 'update', itemId: mediaFor.id, patch: { mediaAssetId, mediaInMs: null, mediaOutMs: null } }]);
            if (ok) setMediaFor(null);
          }}
        />
      )}
      {dialog === 'removed' && (
        <div className="dialog-backdrop" onClick={() => setDialog(null)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-title">
              {t('removed')} <button onClick={() => setDialog(null)}>✕</button>
            </div>
            <div className="dialog-body">
              {removed.length === 0 && <p className="muted">—</p>}
              <table className="grid">
                <tbody>
                  {removed.map((r) => (
                    <tr key={r.id}>
                      <td className="num">{r.displayNumber ?? '-'}</td>
                      <td>{r.title}</td>
                      <td className="c">{new Date(r.removedAt).toLocaleTimeString('el-GR')}</td>
                      <td className="c">
                        <button className="btn" onClick={() => mutate([{ op: 'restore', itemId: r.id }]).then(() => setDialog(null))}>
                          {t('restore')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function RowMenu({ item, canReorder, onAction, onGraphics }: { item: RundownItemDto; canReorder: boolean; onAction: (m: Mutation) => void; onGraphics: () => void }) {
  const [open, setOpen] = useState(false);
  if (!canReorder) return null;
  return (
    <span style={{ position: 'relative' }}>
      <button className="icon-more" title={t('rowMore')} onClick={(e) => (e.stopPropagation(), setOpen((o) => !o))} />
      {open && (
        <div onMouseLeave={() => setOpen(false)} style={{ position: 'absolute', zIndex: 20, background: '#fff', border: '1px solid #8c8c6e', boxShadow: '0 2px 8px rgba(0,0,0,.2)', textAlign: 'left', minWidth: 190, fontSize: 11 }}>
          <MenuItem label={item.floated ? t('unfloat') : t('float')} onClick={() => onAction({ op: 'float', itemId: item.id, floated: !item.floated })} />
          {item.storyId && <MenuItem label={t('duplicateLinked')} onClick={() => onAction({ op: 'duplicate', itemId: item.id, mode: 'linked' })} />}
          <MenuItem label={t('duplicateCopy')} onClick={() => onAction({ op: 'duplicate', itemId: item.id, mode: 'copy' })} />
          <MenuItem label={`+ ${t('separator')}`} onClick={() => onAction({ op: 'insert', afterItemId: item.id, storyId: null, kind: 'separator', title: '.', displayNumber: null })} />
          {item.kind === 'separator' && <MenuItem label={`✕ ${t('separatorRemove')}`} onClick={() => onAction({ op: 'remove', itemId: item.id })} />}
          {item.storyId && <MenuItem label={`${t('colGraphics')}…`} onClick={onGraphics} />}
        </div>
      )}
    </span>
  );
}

/** The small "＋ ⎯" on a row's lower edge (shown on hover): a separator right after that row. */
function SeparatorAdd({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="sep-add"
      title={t('separatorAddAfter')}
      draggable={false}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      ＋ ⎯
    </button>
  );
}

function MenuItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div
      role="menuitem"
      style={{ padding: '4px 8px', cursor: 'pointer' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#e3ecf5')}
      onMouseLeave={(e) => (e.currentTarget.style.background = '')}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {label}
    </div>
  );
}
